/**
 * VG Videos — the slider behaviour and lightbox playback for the product-page
 * "VG Videos" block.
 *
 * The row itself is a native overflow scroller (see vg-videos.css), so this
 * only has to drive the arrows and keep their state in sync with the scroll
 * position. The arrows hide themselves whenever every video already fits,
 * which is also when "Loop slider" is ignored.
 */
class VgVideos extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;

    this.track = this.querySelector('.vg-videos__track');
    if (!this.track) return;

    this.initialised = true;
    this.items = Array.from(this.querySelectorAll('.vg-videos__item'));
    this.prevBtn = this.querySelector('.vg-videos__nav--prev');
    this.nextBtn = this.querySelector('.vg-videos__nav--next');
    this.loop = this.dataset.loop === 'true';
    this.rtl = document.dir === 'rtl';

    this.addListeners();
    this.observeVisibility();
    this.update();
  }

  addListeners() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollByPage(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollByPage(1));

    // Coalesced to one call per frame — 'scroll' fires far more often than the
    // arrow states can meaningfully change.
    let frame = null;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        this.update();
      });
    };

    this.track.addEventListener('scroll', schedule, { passive: true });

    if ('ResizeObserver' in window) {
      new ResizeObserver(schedule).observe(this.track);
    } else {
      window.addEventListener('resize', schedule);
    }
  }

  /**
   * Plays videos as they scroll into sight and pauses them again on the way
   * out, so only what's on screen is decoding. The videos also carry their own
   * 'autoplay' attribute, so the first ones visible start without waiting on
   * this; the observer's job is to stop the rest of the row from decoding
   * off-screen and to start them when they arrive.
   */
  observeVisibility() {
    if (this.dataset.autoplay !== 'true' || !('IntersectionObserver' in window)) return;

    const videos = Array.from(this.querySelectorAll('video'));
    if (!videos.length) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const played = entry.target.play();
          if (played) played.catch(() => { /* Blocked by the browser. */ });
        } else {
          entry.target.pause();
        }
      });
    }, { threshold: 0.25 });

    videos.forEach((video) => io.observe(video));
  }

  /**
   * Distance from the start of the scroller, as a positive number in both
   * reading directions.
   * @returns {number}
   */
  get position() {
    return Math.abs(this.track.scrollLeft);
  }

  /**
   * @returns {number} - Scrollable overflow, 0 when every video fits.
   */
  get maxPosition() {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  }

  /**
   * Scrolls one item along, wrapping around when "Loop slider" is on.
   * @param {number} direction - 1 to advance, -1 to go back.
   */
  scrollByPage(direction) {
    const max = this.maxPosition;
    if (max < 1) return;

    const step = this.items.length > 1
      ? Math.abs(this.items[1].offsetLeft - this.items[0].offsetLeft)
      : this.track.clientWidth;

    let target = this.position + step * direction;

    if (this.loop) {
      // 1px of slack: sub-pixel item widths mean the scroller rarely lands
      // exactly on 0 or on its maximum.
      if (direction > 0 && this.position >= max - 1) {
        target = 0;
      } else if (direction < 0 && this.position <= 1) {
        target = max;
      }
    }

    target = Math.max(0, Math.min(max, target));
    this.track.scrollTo({ left: this.rtl ? target * -1 : target });
  }

  update() {
    if (!this.prevBtn && !this.nextBtn) return;

    const max = this.maxPosition;
    const fits = max < 1;
    const atStart = this.position <= 1;
    const atEnd = this.position >= max - 1;

    [this.prevBtn, this.nextBtn].forEach((btn) => {
      if (btn) btn.hidden = fits;
    });

    if (this.prevBtn) this.prevBtn.disabled = !this.loop && atStart;
    if (this.nextBtn) this.nextBtn.disabled = !this.loop && atEnd;
  }
}

customElements.define('vg-videos', VgVideos);

/**
 * Starts the lightbox video when its dialog opens.
 *
 * The dialogs are the theme's own '<modal-dialog>' elements, so the overlay,
 * focus trap and scroll lock come for free — but 'Modal.open()' also calls
 * 'pauseAllMedia()', which stops the very video the shopper just asked for.
 * Watching the 'open' attribute lands after that call (mutation records are
 * delivered once 'open()' has returned), so playback isn't immediately undone.
 *
 * @param {Element} [root=document] - Element to search within.
 */
function initVgVideoDialogs(root = document) {
  root.querySelectorAll('.vg-videos-modal').forEach((dialog) => {
    if (dialog.dataset.vgObserved === 'true') return;

    const video = dialog.querySelector('video');
    if (!video) return;

    dialog.dataset.vgObserved = 'true';

    new MutationObserver(() => {
      if (dialog.hasAttribute('open')) {
        const played = video.play();
        if (played) played.catch(() => { /* Blocked by the browser. */ });
      } else {
        video.pause();
        video.currentTime = 0;
      }
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
  });
}

initVgVideoDialogs();

// Blocks added or reordered in the theme editor arrive without observers.
document.addEventListener('shopify:section:load', (evt) => initVgVideoDialogs(evt.target));
